// Shared wording and detection logic for class grouping, so every screen speaks the
// same language to admins regardless of which modal or page they are on.

export const DEFAULT_ARM_LABEL = "Arm";

// The school can rename "Arm" (e.g. "Stream", "Section"); older schools saved the
// previous default "Subclass", which reads as jargon, so treat it as unset.
export const getArmLabel = (school) => {
  const saved = (school?.settings?.subclass_label || "").trim();
  if (!saved || saved.toLowerCase() === "subclass") return DEFAULT_ARM_LABEL;
  return saved;
};

export const pluralize = (label = DEFAULT_ARM_LABEL, count = 2) => {
  if (count === 1) return label;
  const lower = label.toLowerCase();
  if (/(s|x|z|ch|sh)$/.test(lower)) return `${label}es`;
  return `${label}s`;
};

// "JSS 1A" -> { base: "JSS 1", label: "A" }; "Primary 2 Gold" -> { base: "Primary 2", label: "GOLD" }.
// Returns null when the name has no recognisable arm suffix after a number.
export const splitArmName = (className) => {
  const name = (className || "").trim();
  const match = name.match(/^(.*\d)\s*[-_/]?\s*([A-Za-z]{1,10})$/);
  if (!match) return null;
  const base = match[1].trim();
  const label = match[2];
  if (!base || label.toLowerCase().startsWith("class")) return null;
  return { base, label: label.toUpperCase() };
};

// Finds ungrouped classes whose names look like arms of the same level, e.g.
// JSS 1A / JSS 1B / JSS 1C -> [{ base: "JSS 1", classes: [...] }].
export const detectArmGroups = (classes = []) => {
  const byBase = new Map();
  classes.forEach((cls) => {
    if (cls.class_group_id) return;
    const split = splitArmName(cls.class_name);
    if (!split) return;
    const key = split.base.toLowerCase();
    if (!byBase.has(key)) byBase.set(key, { base: split.base, classes: [] });
    byBase.get(key).classes.push(cls);
  });
  return Array.from(byBase.values()).filter((g) => g.classes.length >= 2);
};

export const getTeacherName = (teachers = [], teacherId) => {
  if (!teacherId) return null;
  const t = teachers.find((teacher) => String(teacher._id) === String(teacherId));
  if (!t) return "Unknown teacher";
  if (t.user?.firstname || t.user?.lastname) {
    return `${t.user.firstname || ""} ${t.user.lastname || ""}`.trim();
  }
  if (t.firstname || t.lastname) return `${t.firstname || ""} ${t.lastname || ""}`.trim();
  return t.title || t.email || "Teacher";
};

// Common school-subject abbreviations, so "Maths" and "Mathematics" are recognised
// as the same subject when arms were set up by different people.
const SUBJECT_ALIASES = {
  math: "mathematics",
  maths: "mathematics",
  englishlang: "englishlanguage",
  english: "englishlanguage",
  crs: "christianreligiousstudies",
  crk: "christianreligiousstudies",
  irs: "islamicreligiousstudies",
  irk: "islamicreligiousstudies",
  phe: "physicalandhealtheducation",
  ict: "informationandcommunicationtechnology",
  agric: "agriculturalscience",
  bst: "basicscienceandtechnology",
};

const subjectKey = (name) => {
  const compact = (name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return SUBJECT_ALIASES[compact] || compact;
};

export const areSimilarSubjectNames = (a, b) => {
  // Identically named subjects are already treated as one subject.
  if ((a || "").trim().toLowerCase() === (b || "").trim().toLowerCase()) return false;
  const ka = subjectKey(a);
  const kb = subjectKey(b);
  if (!ka || !kb) return false;
  if (ka === kb) return true;
  const [shorter, longer] = ka.length <= kb.length ? [ka, kb] : [kb, ka];
  return shorter.length >= 4 && longer.startsWith(shorter);
};
